import React, { useMemo, useState } from "react";
import "./Newsletter.css";
import Wrapper from "../components/basic/wrapper";
import SafeArea from "../components/basic/safe-area";
import { useI18n } from "../i18n/i18n";

const Newsletter: React.FC = () => {
    const sectionColor = "#000000";
    const sectionTextColor = "#ffffff";

    const { t } = useI18n();

    const [email, setEmail] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [toast, setToast] = useState<{ type: "success" | "error"; message: string } | null>(null);

    const emailRegex = useMemo(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/, []);

    const showToast = (type: "success" | "error", message: string) => {
        setToast({ type, message });
        window.clearTimeout((showToast as any)._t);
        (showToast as any)._t = window.setTimeout(() => setToast(null), 2500);
    };

    const validate = (value: string) => {
        const v = value.trim();
        if (!v) return t("newsletter.validation.required");
        if (!emailRegex.test(v)) return t("newsletter.validation.invalid");
        return null;
    };

    const handleSubmit = async () => {
        const validationError = validate(email);
        setError(validationError);

        if (validationError) {
            showToast("error", validationError);
            return;
        }

        try {
            setIsSubmitting(true);

            // ✅ TODO: replace with your real API call
            // await fetch('/api/newsletter', { method: 'POST', body: JSON.stringify({ email }) });

            // Fake delay
            await new Promise((r) => setTimeout(r, 400));

            setEmail("");
            setError(null);
            showToast("success", t("newsletter.toast.success"));
        } catch (e) {
            showToast("error", t("newsletter.toast.error"));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Wrapper
            hasFooter={false}
            color={sectionColor}
            footerColor={sectionTextColor}
            logoColor={sectionTextColor}
        >
            <div style={{ backgroundColor: sectionColor }} className="newsletter-wrapper">
                <SafeArea>
                    <>
                        <div className="fs48 fw700 newsletter-title">{t("newsletter.title")}</div>

                        {/* GRID (12 columns) */}
                        <div className="newsletter-grid">
                            {/* Left */}
                            <div className="newsletter-left">
                                <div className="newsletter-heading">{t("newsletter.heading")}</div>

                                <div className="newsletter-subtitle">{t("newsletter.subtitle")}</div>

                                <div className="newsletter-form">
                                    <input
                                        className={`newsletter-input ${error ? "newsletter-input-error" : ""}`}
                                        type="email"
                                        value={email}
                                        onChange={(e) => {
                                            setEmail(e.target.value);
                                            if (error) setError(null);
                                        }}
                                        onBlur={() => setError(validate(email))}
                                        placeholder={t("newsletter.placeholder")}
                                        disabled={isSubmitting}
                                    />

                                    <button
                                        className="newsletter-btn"
                                        type="button"
                                        onClick={handleSubmit}
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? t("newsletter.ctaSubmitting") : t("newsletter.cta")}
                                    </button>
                                </div>

                                {error && <div className="newsletter-error">{error}</div>}
                            </div>

                            {/* Right */}
                            <div className="newsletter-right">
                                <div className="newsletter-img-box">
                                    <img
                                        src="https://images-extras.s3.us-east-1.amazonaws.com/newsletter.png"
                                        alt={t("newsletter.imageAlt")}
                                        className="newsletter-img"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Toast */}
                        {toast && (
                            <div
                                className={`newsletter-toast ${toast.type === "success" ? "is-success" : "is-error"}`}
                            >
                                {toast.message}
                            </div>
                        )}
                    </>
                </SafeArea>
            </div>
        </Wrapper>
    );
};

export default Newsletter;
